/**
 * 大阪府の背景ツアー「大阪めぐり」コンテンツ。
 *
 * 軸は一日の時間。冬の早朝の住吉大社から、夜の新世界へ。
 *
 * 著作権・商標について。大阪は特に注意がいる。
 *  - 道頓堀の看板・ネオン・立体看板は絵にも文章にも出さない。そもそも道頓堀は採っていない。
 *  - 万博記念公園の太陽の塔は岡本太郎の著作物。描かせない。日本庭園と森だけを扱い、
 *    英語記述に「塔・彫刻・像を描かない」という否定文を入れている。
 *  - 通天閣の絵では、周囲の企業広告・ロゴ・文字・マスコットを一切描かせない。
 *    新世界の店の看板文字も描かせない（実在店名が生成されるため）。
 *  - 特定企業のテーマパーク、キャラクター、球団、商品名は出さない。
 *
 * 仁徳天皇陵古墳について。
 *  陵墓は宮内庁が管理する祭祀の場所なので、静かに扱う。はしゃいだ書き方をしない。
 *  上空からの鍵穴形は描かせない。地上の濠ぎわから森を見る構図だけにする。
 *  拝所には鳥居があるが、この構図には入らないので鳥居の否定文を入れている。
 *
 * 鳥居について。
 *  住吉大社は鳥居があるので否定文を入れていない。
 *  四天王寺は西門に石鳥居があるが、南から中心伽藍を見通す構図には入らないので否定文を入れている。
 *  犬鳴山は渓谷と滝だけを描くので否定文を入れている。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 百舌鳥・古市古墳群の構成資産数、仁徳天皇陵古墳の墳丘長・三重の濠はいずれも未確認。書かない。
 *  - 箕面大滝の落差、通天閣の設計者、大阪城の個々の巨石の名称も未確認。書かない。
 *  - 入館料、営業時間、来場者数、通行止め情報など時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const osakaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'sumiyoshi-taisha',
      name: '住吉大社',
      area: '大阪市住吉区・冬の早朝',
      image: '/assets/scenery/osaka/scenes/01_sumiyoshi_taisha.webp',
      hotspots: [
        {
          id: 'sumiyoshi-kokuho',
          x: 0.46,
          y: 0.4,
          title: '四棟すべてが国宝',
          body: '住吉大社の本殿は第一本宮から第四本宮まで四棟あり、そのすべてが国宝に指定されています。第一・第二・第三本宮が縦に直列し、第三と第四が横に並ぶ配置で、公式には大海原をゆく船団にたとえられています。',
          source: 'https://www.sumiyoshitaisha.net/about/architect.html',
          mapQuery: '住吉大社 本殿',
        },
        {
          id: 'sumiyoshi-zukuri',
          x: 0.74,
          y: 0.5,
          title: '住吉造という古い様式',
          body: '住吉造は神社建築史上最古の様式のひとつとされます。屋根は檜の皮を重ねた檜皮葺の妻入式切妻造、柱は丹塗りの朱、壁は貝殻を磨りつぶした胡粉塗りの白。現存する本殿は文化七年の造営です。',
          source: 'https://www.sumiyoshitaisha.net/about/architect.html',
          mapQuery: '住吉大社 第一本宮',
        },
        {
          id: 'sumiyoshi-sorihashi',
          x: 0.22,
          y: 0.48,
          title: '反橋（太鼓橋）',
          body: '反橋は太鼓橋とも呼ばれ、公式サイトでは住吉の象徴とされる橋として紹介されています。境内には国宝の四本殿のほか、樹齢1000年を超える御神木もあります。',
          source: 'https://www.sumiyoshitaisha.net/',
          mapQuery: '住吉大社 反橋',
        },
      ],
    },
    {
      id: 'inunakisan',
      name: '犬鳴山 七宝瀧寺',
      area: '泉佐野市・初夏の朝',
      image: '/assets/scenery/osaka/scenes/02_inunakisan.webp',
      hotspots: [
        {
          id: 'inunaki-661',
          x: 0.46,
          y: 0.42,
          title: '役行者が28歳のときに開いた',
          body: '七宝瀧寺は、斉明天皇七年（661年）、修験道の開祖である役行者が28歳のときに開基したと伝わります。山内全体が大きな行場とされ、いまも葛城修験の入峯修行や山内の行場での修行が続けられています。',
          source: 'https://www.inunakisan.jp/about',
          mapQuery: '犬鳴山 七宝瀧寺',
        },
        {
          id: 'inunaki-giken',
          x: 0.74,
          y: 0.34,
          title: '犬鳴山という名の由来',
          body: '寛平二年（890年）、猟師の愛犬が大蛇から主人を守って戦ったという逸話が伝わります。その忠義を宇多天皇が報恩の義犬よと賞し、山を犬鳴山と名づけたとされています。',
          source: 'https://www.inunakisan.jp/about',
          mapQuery: '犬鳴山',
        },
        {
          id: 'inunaki-28shuku',
          x: 0.22,
          y: 0.5,
          title: '葛城二十八宿の行場',
          body: '役行者はこの地に法華経二十八品を一品ずつ各所に埋納して経塚を築き、入峯修行者のために二十八か所の参籠行場をつくったと伝わります。山内全体が大きな行場で、そのなかに行者の瀧があります。',
          source: 'https://www.inunakisan.jp/about',
          mapQuery: '犬鳴山 行者の滝',
        },
      ],
    },
    {
      id: 'shitennoji',
      name: '四天王寺',
      area: '大阪市天王寺区・春の午前',
      image: '/assets/scenery/osaka/scenes/03_shitennoji.webp',
      hotspots: [
        {
          id: 'shitennoji-593',
          x: 0.46,
          y: 0.44,
          title: '推古天皇元年の創建',
          body: '四天王寺は推古天皇元年（593年）に建立されました。公式には、聖徳太子が自ら四天王像を彫り、勝利したら四天王を安置する寺院を建立して人々を救済すると誓願し、その誓いを果たして創建したと伝わるとされています。',
          source: 'https://www.shitennoji.or.jp/history.html',
          mapQuery: '四天王寺',
        },
        {
          id: 'shitennoji-garan',
          x: 0.74,
          y: 0.34,
          title: '四天王寺式伽藍配置',
          body: '南から北へ向かって中門、五重塔、金堂、講堂を一直線に並べ、それを回廊が囲みます。これを四天王寺式伽藍配置と呼び、日本では最も古い建築様式のひとつとされます。六世紀から七世紀の大陸の様式を今日に伝えるものです。',
          source: 'https://www.shitennoji.or.jp/history.html',
          mapQuery: '四天王寺 中心伽藍',
        },
        {
          id: 'shitennoji-kanji',
          x: 0.22,
          y: 0.5,
          title: '日本仏法最初の官寺とされる',
          body: '公式サイトは四天王寺を、聖徳太子が建立した日本仏法最初の官寺と記しています。境内には聖徳太子にまつわる堂宇が並びます。',
          source: 'https://www.shitennoji.or.jp/',
          mapQuery: '四天王寺 五重塔',
        },
      ],
    },
    {
      id: 'tondabayashi',
      name: '富田林寺内町',
      area: '富田林市・昼前',
      image: '/assets/scenery/osaka/scenes/04_tondabayashi.webp',
      hotspots: [
        {
          id: 'tondabayashi-judenken',
          x: 0.46,
          y: 0.42,
          title: '大阪府で唯一の保存地区',
          body: '富田林市の富田林地区は、平成9年10月31日に国の重要伝統的建造物群保存地区に選定されました。種別は寺内町・在郷町、面積は12.9ヘクタール。大阪府内で選定されているのはこの一か所だけです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '富田林寺内町',
        },
        {
          id: 'tondabayashi-koshoji',
          x: 0.74,
          y: 0.34,
          title: '町の中核となった興正寺別院',
          body: '永禄初年ごろ、京都興正寺第十六世の証秀上人が石川左岸の荒地を購入して寺内町の開発に着手し、その中核となる寺院を建てました。平成26年に本堂・対面所・鐘楼・鼓楼・山門・御成門が国の重要文化財に指定されています。',
          source: 'https://tondabayashi-trip.com/spot_detail.html?id=146',
          mapQuery: '富田林 興正寺別院',
        },
        {
          id: 'tondabayashi-sugiyama',
          x: 0.22,
          y: 0.5,
          title: '寺内町で最古級とされる旧杉山家住宅',
          body: '旧杉山家住宅は昭和58年に国の重要文化財に指定された町屋建築で、富田林市の観光サイトは寺内町のなかでも最古級と紹介しています。杉山家は寺内町の創設にも関わったとされ、造り酒屋を営むなどして旧家として栄えました。',
          source: 'https://tondabayashi-trip.com/spot_detail.html?id=115',
          mapQuery: '旧杉山家住宅',
        },
      ],
    },
    {
      id: 'mozu-kofun',
      name: '仁徳天皇陵古墳と百舌鳥古墳群',
      area: '堺市堺区・初夏の昼',
      image: '/assets/scenery/osaka/scenes/05_mozu_kofun.webp',
      hotspots: [
        {
          id: 'mozu-sekaiisan',
          x: 0.46,
          y: 0.44,
          title: '令和元年7月の世界遺産登録',
          body: '令和元年7月6日、第43回世界遺産委員会において、百舌鳥・古市古墳群の世界遺産一覧表への記載が決定しました。保存と活用は、大阪府・堺市・羽曳野市・藤井寺市の四者による協議会が担っています。',
          source: 'https://www.city.sakai.lg.jp/kanko/rekishi/sei/kohun_siru/torikumi/index.html',
          mapQuery: '百舌鳥古墳群',
        },
        {
          id: 'mozu-44ki',
          x: 0.74,
          y: 0.36,
          title: '東西南北およそ4キロに44基',
          body: '百舌鳥古墳群は堺市内の東西・南北およそ4キロの範囲に広がります。古墳時代にはかつて百基を超える古墳が築かれましたが、現在残るのは四世紀後半から六世紀前半の44基です。',
          source:
            'https://www.city.sakai.lg.jp/kanko/rekishi/sei/kohun_siru/kohun_gaiyo/index.html',
          mapQuery: '仁徳天皇陵古墳',
        },
        {
          id: 'mozu-furuichi',
          x: 0.22,
          y: 0.5,
          title: '古市古墳群とあわせてひとつの遺産',
          body: '古市古墳群は藤井寺市から羽曳野市にかけて、やはり東西・南北およそ4キロに広がります。墳丘長200メートルを超える巨大な前方後円墳七基を含む45基が残ります。',
          source:
            'https://www.city.sakai.lg.jp/kanko/rekishi/sei/kohun_siru/kohun_gaiyo/index.html',
          mapQuery: '古市古墳群',
        },
      ],
    },
    {
      id: 'osaka-jo',
      name: '大阪城',
      area: '大阪市中央区・春の午後',
      image: '/assets/scenery/osaka/scenes/06_osaka_jo.webp',
      hotspots: [
        {
          id: 'osakajo-1931',
          x: 0.46,
          y: 0.36,
          title: '市民の寄付で建った天守',
          body: '現在の天守閣は昭和6年、市民の寄付金によって復興されました。平成9年に国の登録有形文化財となり、令和7年には大阪市指定有形文化財にもなっています。',
          source: 'https://www.osakacastle.net/',
          mapQuery: '大阪城天守閣',
        },
        {
          id: 'osakajo-ishigaki',
          x: 0.74,
          y: 0.52,
          title: '石垣も堀も、すべて幕府の再築',
          body: '豊臣秀吉が築いた大坂城は、大坂夏の陣のあと大量の盛り土で地中に埋められました。公式は、いまの大阪城の石垣や堀はすべて幕府による再築時のものと明記しています。豊臣期の石垣を地上で見ることはできません。',
          source: 'https://www.osakacastle.net/toyotomi_stone_wall/',
          mapQuery: '大阪城 豊臣石垣館',
        },
        {
          id: 'osakajo-shiseki',
          x: 0.22,
          y: 0.48,
          title: '城域そのものが特別史跡',
          body: '大阪城一帯は国の特別史跡に指定されており、園内に残る古建造物のほとんどが重要文化財に指定されています。',
          source: 'https://www.osakacastle.net/',
          mapQuery: '大阪城公園',
        },
      ],
    },
    {
      id: 'expo-teien',
      name: '万博記念公園 日本庭園',
      area: '吹田市・秋の午後',
      image: '/assets/scenery/osaka/scenes/07_expo_teien.webp',
      hotspots: [
        {
          id: 'expo-yonyoshiki',
          x: 0.46,
          y: 0.44,
          title: '四つの造園様式をたどる庭',
          body: '日本庭園は、日本万国博覧会に政府出展施設として、日本の造園技術の粋を集めてつくられた庭園です。西から東へ流れるせせらぎに沿って、上代から中世、近世、現代へと四つの造園様式を取り入れています。',
          source: 'https://www.expo70-park.jp/facility/japanese-garden/',
          mapQuery: '万博記念公園 日本庭園',
        },
        {
          id: 'expo-shinjiike',
          x: 0.74,
          y: 0.34,
          title: '東西1300メートルの広さ',
          body: '庭園の面積は26ヘクタール、東西1300メートル、南北200メートルに及びます。園内の心字池は、草書体の心という文字の形をした池で、ここから池泉回遊式庭園の景観を眺めることができます。',
          source: 'https://www.expo70-park.jp/facility/japanese-garden/',
          mapQuery: '万博記念公園 心字池',
        },
        {
          id: 'expo-komorebi',
          x: 0.22,
          y: 0.5,
          title: '17トンの石を使った滝',
          body: '木漏れ日の滝は、高さ3.5メートルの二段落ちの滝を中心に、左に一つ、右に二つ、形の違う小滝を組み合わせてつくられています。主滝に使われている石は一個で17トンという巨石です。',
          source: 'https://www.expo70-park.jp/facility/japanese-garden/',
          mapQuery: '万博記念公園 木漏れ日の滝',
        },
      ],
    },
    {
      id: 'minoh-otaki',
      name: '箕面大滝',
      area: '箕面市・晩秋の夕方前',
      image: '/assets/scenery/osaka/scenes/08_minoh_otaki.webp',
      hotspots: [
        {
          id: 'minoh-kokutei',
          x: 0.46,
          y: 0.4,
          title: '明治の森箕面国定公園',
          body: '箕面大滝は明治の森箕面国定公園のなかにあります。箕面市観光協会は箕面山を、箕面大滝で知られる明治の森箕面国定公園と紹介しており、日本の滝百選にも選ばれています。',
          source: 'https://minohkankou.net/about/page-01/',
          mapQuery: '箕面大滝',
        },
        {
          id: 'minoh-mi',
          x: 0.74,
          y: 0.5,
          title: '「箕」に似ているから箕面',
          body: '滝の名の由来には諸説ありますが、一説には、滝が流れ落ちる面が、穀物をもみ殻からふるい分ける農具の「箕」に似ていることによるとされます。いまの市名もここから来ています。',
          source: 'https://minohkankou.net/about/page-01/',
          mapQuery: '箕面山',
        },
        {
          id: 'minoh-enno',
          x: 0.22,
          y: 0.48,
          title: '役行者が悟りを開いた滝',
          body: 'およそ1300年前、修験道の開祖である役行者がこの箕面大滝で修行を行い、弁財天の導きを受けて真理を悟ったと伝えられます。滝は平安期の歌謡集や鴨長明の歌にも現れます。',
          source: 'https://minohkankou.net/about/page-01/',
          mapQuery: '箕面 瀧安寺',
        },
      ],
    },
    {
      id: 'nakanoshima',
      name: '中之島 大阪市中央公会堂',
      area: '大阪市北区・初夏の夕暮れ',
      image: '/assets/scenery/osaka/scenes/09_nakanoshima.webp',
      hotspots: [
        {
          id: 'nakanoshima-futatsu',
          x: 0.46,
          y: 0.44,
          title: '北に堂島川、南に土佐堀川',
          body: '大阪市中央公会堂は、北には堂島川、南には土佐堀川が流れる中之島に建ちます。ネオ・ルネサンス様式の外観は、水辺の青や木々の緑と調和すると公式に記されています。',
          source: 'https://osaka-chuokokaido.com/ja/about',
          mapQuery: '大阪市中央公会堂',
        },
        {
          id: 'nakanoshima-iwamoto',
          x: 0.74,
          y: 0.34,
          title: '一市民の寄付から始まった',
          body: '株式仲買人の岩本栄之助は、渡米実業団でアメリカの富豪が財産を公共事業に投じていることに感銘を受け、帰国後、父の遺産五十万円に自分の財産を加えた百万円を大阪市に寄付しました。',
          source: 'https://osaka-chuokokaido.com/ja/history',
          mapQuery: '中之島公園',
        },
        {
          id: 'nakanoshima-1918',
          x: 0.22,
          y: 0.5,
          title: '設計競技で選ばれた案',
          body: '懸賞金付きの設計競技が行われ、早稲田大学教授・岡田信一郎の案が一等に選ばれました。延べ18万4千人の職人と5年の歳月をかけて大正7年に完成し、平成14年12月に国の重要文化財に指定されました。',
          source: 'https://osaka-chuokokaido.com/ja/history',
          mapQuery: '中之島 中央公会堂',
        },
      ],
    },
    {
      id: 'tsutenkaku',
      name: '通天閣と新世界',
      area: '大阪市浪速区・夜',
      image: '/assets/scenery/osaka/scenes/10_tsutenkaku.webp',
      hotspots: [
        {
          id: 'tsutenkaku-1912',
          x: 0.46,
          y: 0.3,
          title: '初代は明治45年に建った',
          body: '初代の通天閣は1912年（明治45年）に完成しました。高さは250尺、およそ75メートルで、当時の日本でいちばん高い建物でした。',
          source: 'https://www.tsutenkaku.co.jp/pdf/Tsutenkaku60-rekishi.pdf',
          mapQuery: '新世界',
        },
        {
          id: 'tsutenkaku-1956',
          x: 0.74,
          y: 0.5,
          title: 'いまの塔は昭和31年開業',
          body: '現在の二代目通天閣は1956年（昭和31年）10月28日に開業しました。高さはおよそ103メートルで、当時の大阪でいちばん高い建物でした。',
          source: 'https://www.tsutenkaku.co.jp/pdf/Tsutenkaku60-rekishi.pdf',
          mapQuery: '通天閣',
        },
        {
          id: 'tsutenkaku-toroku',
          x: 0.22,
          y: 0.48,
          title: '国の登録有形文化財',
          body: '通天閣は2007年（平成19年）に国の登録有形文化財となりました。これは歴史上、芸術上、学術上の価値が高い建造物に与えられるものです。',
          source: 'https://www.tsutenkaku.co.jp/pdf/Tsutenkaku60-rekishi.pdf',
          mapQuery: '通天閣 展望台',
        },
      ],
    },
  ],
};
